import type {ReactElement} from 'react';
import {useCallback} from 'react';
import {Text, TextSize} from '@Components/text';
import type {QRCodeFileExtension} from '@Libraries/qr-code/qr-code.types';
import {QRPreviewPanel} from '@Components/qr-code-generator/components/qr-preview-panel/qr-preview-panel';
import {QRCodeCarousel} from '@Components/qr-code-generator/components/qr-code-carousel/qr-code-carousel';
import {QRCodeActions} from '@Components/qr-code-generator/components/qr-code-actions/qr-code-actions';
import {useIsCompactQRTool} from '@Components/qr-code-generator/qr-code-generator-hooks';
import styles from './qr-code-result.module.scss';
import type {QRCodeResultProps} from './qr-code-result.types';

export function QRCodeResult({
  code,
  previewUrl,
  isRendering,
  isCodeBlocked,
  payloadIssueText,
  variantLabel,
  carousel = null,
  customizeControl = null,
  onDownload,
  onCopy,
}: QRCodeResultProps): ReactElement {
  const isCompact = useIsCompactQRTool();

  const onDownloadCode = useCallback(
    async (extension: QRCodeFileExtension): Promise<void> => {
      await onDownload(code, extension);
    },
    [code, onDownload]
  );

  const onCopyCode = useCallback(async (): Promise<void> => {
    await onCopy(code);
  }, [code, onCopy]);

  const renderVariantLabel = (): ReactElement | null => {
    if (variantLabel === null) {
      return null;
    }
    return <Text val={variantLabel} size={isCompact ? TextSize.SM : TextSize.MD} bold className={styles.qrCodeResultLabel} />;
  };

  return (
    <div className={styles.qrCodeResult}>
      <div className={styles.qrCodeResultStage}>
        <div className={styles.qrCodeResultCode}>
          {renderVariantLabel()}
          <QRCodeCarousel controls={carousel}>
            <QRPreviewPanel previewUrl={previewUrl} isRendering={isRendering} payloadIssueText={payloadIssueText} />
          </QRCodeCarousel>
        </div>
      </div>
      <QRCodeActions isCodeBlocked={isCodeBlocked} payloadIssueText={payloadIssueText} customizeControl={customizeControl} onDownload={onDownloadCode} onCopy={onCopyCode} />
    </div>
  );
}
